/* eslint-disable import/no-unresolved */
import BaseElement from 'base-element'
import { css, html, ref, createRef } from 'lit-all'
import styles from 'lib-styles'
import { debounce } from 'lib-utils'
import * as paths from 'lib-paths'
/* eslint-disable import/no-unresolved */

class HHProductStrip extends BaseElement {
  static properties = {
    variants: {
      type: Object,
    },
    url: {
      type: String,
    },
    title: {
      type: String,
    },
    subCollection: {
      type: String,
      attribute: 'sub-collection',
    },
    productId: {
      type: String,
      attribute: 'product-id',
    },
    productUrl: {
      type: String,
      attribute: 'product-url',
    },
    bundle: {
      type: String,
    },
    sizeInfo: {
      type: String,
      attribute: 'size-info',
    },
    description: {
      type: String,
    },
    tagInfo: {
      type: String,
      attribute: 'tag-info',
    },
    linkText: {
      type: String,
      attribute: 'link-text',
    },
    swatches: {
      type: Array,
    },
    variantsScroll: {
      type: Number,
      state: true,
    },
    variantsScrollWidth: {
      type: Number,
      state: true,
    },
    variantsClientWidth: {
      type: Number,
      state: true,
    },
  }

  $variants = createRef()

  constructor() {
    super()
    this.variantsScroll = 0
  }

  connectedCallback() {
    super.connectedCallback()
    this.MAX_VARIANTS_FOR_CAROUSEL = 2 // todo: change to 3 for desktop
  }

  firstUpdated() {
    this.firstCardElement = this.querySelector('hh-product-card')
    this.variantsScrollWidth = this.$variants.value.scrollWidth
    this.variantsClientWidth = this.$variants.value.clientWidth
    this.cardWidth = this.firstCardElement?.getBoundingClientRect().width
  }

  updated() {
    this._setScrollValues()
  }

  _handleLeftArrowClick(event) {
    let newPosition = this.$variants.value.scrollLeft - this.cardWidth
    this.$variants.value.scrollLeft = newPosition
    if (Math.floor(newPosition) <= 0) {
      newPosition = 0
      this.$variants.value.scrollLeft = newPosition
    }
    this.variantsScroll = newPosition
    this._setScrollValues()
  }

  _handleRightArrowClick(event) {
    let newPosition = this.$variants.value.scrollLeft + this.cardWidth
    this.$variants.value.scrollLeft = newPosition
    this.variantsScroll = newPosition
    this._setScrollValues()
  }

  _setScrollValues() {
    this.variantsScrollWidth = this.$variants.value.scrollWidth
    this.variantsClientWidth = this.$variants.value.clientWidth
  }

  _handleVariantsScroll() {
    this._setScrollValues()
    this.variantsScroll = this.$variants.value.scrollLeft
    const closestSnapPoint =
      Math.round(this.variantsScroll / this.cardWidth) * this.cardWidth
    this.$variants.value.scrollLeft = closestSnapPoint
  }

  get hideArrows() {
    if (!this.variantsScrollWidth || !this.variantsClientWidth) return true
    return this.variantsScrollWidth === this.variantsClientWidth
  }

  render() {
    return html`
      <div id="item">
        <div id="content">
          <a href=${this.url} id="title"><h3 class="h1">${this.title}</h3></a>
          ${this.subCollection
            ? ''
            : html`
                <div class="productCard_ratings f fdr aie rel pt5 mb5">
                  <div
                    data-bv-show="inline_rating"
                    data-bv-product-id="{{product_id}}"
                    data-bv-redirect-url="{{ product_url }}"
                    data-bv-seo="false"
                  ></div>
                </div>
              `}
          <div id="info">
            ${this.bundle
              ? ''
              : html`${this.sizeInfo
                  ? html`<p class="h2">${this.sizeInfo}</p>`
                  : ''}`}
            ${this.description
              ? html`<p class="h2">${this.description}</p>`
              : ''}
            ${this.tagInfo?.length
              ? html`
                  ${this.tagInfo.map(
                    (item) => html` <p class="h2">${item}</p> `
                  )}
                `
              : ''}
          </div>
          ${this.linkText
            ? html`<div>
                <a href=${this.url} class="link-text">${this.linkText}</a>
              </div>`
            : ''}
          ${this.variants.length > this.MAX_VARIANTS_FOR_CAROUSEL
            ? html`
                <div id="arrows" ?hidden=${this.hideArrows}>
                  <button
                    @click=${debounce(this._handleLeftArrowClick, 300, true)}
                    class="arrow fade"
                    id="arrow--left"
                    ?disabled=${this.variantsScroll === 0}
                  >
                    ${paths.navigationArrow}
                  </button>
                  <button
                    @click=${debounce(this._handleRightArrowClick, 300, true)}
                    class="arrow fade"
                    id="arrow--right"
                    ?disabled=${this.variantsScroll >
                    this.variantsScrollWidth -
                      this.variantsClientWidth -
                      this.cardWidth}
                  >
                    ${paths.navigationArrow}
                  </button>
                </div>
              `
            : ''}
        </div>
        <div
          id="variants"
          class="no-scrollbar"
          ${ref(this.$variants)}
          @scroll=${debounce(this._handleVariantsScroll, 300)}
        >
          <slot name="product-card"> </slot>
        </div>
      </div>
    `
  }

  static styles = [
    styles.reset,
    styles.a11y,
    styles.flex,
    styles.typography,
    styles.skeleton,
    css`
      p {
        margin: 0;
      }
      .h1 {
        font-family: 'PP Right Grotesk', sans-serif;
        font-weight: 500;
        font-size: 24px;
        margin: 0;
        line-height: 1;
      }
      .h2 {
        font-weight: 500;
      }
      .link-text {
        text-decoration: underline;
      }
      #arrows {
        display: none;
        align-items: center;
        gap: 25px;
        position: absolute;
        bottom: 32px;
        right: 32px;
      }
      .arrow {
        align-items: center;
        background-color: transparent;
        color: var(--color-dark-green, #35332b);
        cursor: pointer;
        display: block;
        padding: 0;
        width: 44px;
        height: 44px;
        transition: opacity 0.2s;
      }
      .arrow[disabled] {
        opacity: 0.5;
        pointer-events: none;
      }
      .arrow svg {
        pointer-events: none;
        width: 100%;
      }
      #item {
        display: flex;
        flex-direction: column;
      }
      #arrow--left {
        right: 50px;
        transform: rotate(180deg);
      }
      #arrow--right {
        right: 0px;
      }
      #content {
        margin-bottom: 15px;
        display: flex;
        gap: 8px;
        flex-direction: column;
        width: 100%;
        padding: 32px 0;
        position: relative;
      }
      #info {
        display: flex;
      }
      #variants {
        display: flex;
        overflow: scroll;
        scroll-behavior: smooth;
        -webkit-overflow-scrolling: touch;
      }
      ::slotted(hh-product-card) {
        width: 100%;
        min-width: 150px;
      }
      @media screen and (min-width: 769px) {
        ::slotted(hh-product-card) {
          width: 340px;
          min-width: 340px;
        }
        #arrows {
          display: flex;
        }
        #variants {
          padding-right: 340px;
        }
      }
      @media screen and (min-width: 1199px) {
        .h1 {
          font-size: 38px;
        }
        .h2 {
          font-size: 20px;
        }
        #item {
          position: relative;
        }
        #info {
          flex-direction: column;
          gap: 8px;
        }
        #variants::-webkit-scrollbar {
          display: none;
        }
        #variants::-webkit-scrollbar {
          display: none;
        }
      }
    `,
  ]
}

customElements.define('hh-product-strip', HHProductStrip)
